import React, { FC, useState, useMemo, useRef, useEffect } from "react"
import { 
    Platform, 
    useWindowDimensions, 
    View, 
    StyleSheet, 
    Pressable, 
    ActivityIndicator, 
    Image,
    AlertDialog as RNAlertDialog 
} from 'react-native';
import { 
    Box, 
    Button, 
    ButtonText, 
    ButtonIcon, 
    HStack, 
    VStack, 
    Text, 
    Icon, 
    Spinner,
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogBackdrop,
    Heading,
    ButtonGroup
} from '@gluestack-ui/themed';
import { MaterialIcons, Ionicons, MaterialCommunityIcons, FontAwesome5 } from "@expo/vector-icons";
import * as ImageManipulator from 'expo-image-manipulator';
import SignatureCanvas from 'react-signature-canvas'
import SignatureScreen from 'react-native-signature-canvas';
import { observer } from "mobx-react-lite"

import { ElementBase, IElementBaseProps, useInitializeElement, ModalFull } from ".."
import { convertImageToBase64 } from '../../utils/general';
import { _findField, checkIsValid } from "../../utils/itemCommonFunctions";

const importImageFromPopulate = async (value, element, itemsStore) => {
    let fileName: string = `${element.type}-${element.id}-${itemsStore.itemActive._startedAt.getTime()}.jpg`;
    let imgB64 = await (new Promise((resolve, reject) => {
        try {
            convertImageToBase64(value, resolve);
        } catch (e) {
            reject();
        }
    }))
    //@ts-ignore
    value = await itemsStore.saveFilePayload(fileName, imgB64);
    return value.uri;
}

export const SignatureElement: FC<IElementBaseProps> = observer(({ ...props }) => {
    const {
        itemsStore, element, modalVisible, setModalVisible, statusStore, authenticationStore,
        loading, setLoading,
        payload, checkAndSetPayload, errorList,
    } = useInitializeElement(props, true, importImageFromPopulate);

    const [saveToCache, setSaveToCache] = useState<boolean>(false);
    const [alertData, setAlertData] = useState<any>(undefined);

    const cancelRef = useRef(null);
    const signRef = useRef<any>(null);
    const screen = useWindowDimensions();

    // Cálculo de dimensiones para el canvas
    const dims = useMemo(() => {
        const width = statusStore.windowDimensions.width - 15;
        const height = (statusStore.windowDimensions.height - 150) > width ? width * 0.66 : (statusStore.windowDimensions.height - 150);
        return { width, height };
    }, [screen, statusStore.windowDimensions]);

    let rndExtraImg = "";
    if (props.parentId && itemsStore.getPayloadElement(props.parentId).type == "simplelist") {
        rndExtraImg = (new Date()).getTime().toString();
    }

    const clear = () => {
        Platform.OS == "web" ? signRef.current?.clear() : signRef.current.clearSignature();
    }

    const getFromCache = () => {
        if (authenticationStore.userInfo.user.signCache == undefined) {
            setAlertData({
                title: "Firma de Perfil",
                message: `No existe configurada una firma en tu perfil ¿Queres crearla ahora? Para cambiarla deberás ingresar desde el menú lateral a Configuraciones.`,
                action: () => {
                    setSaveToCache(true);
                    setModalVisible(true);
                }
            });
        } else {
            setLoading(true);
            convertImageToBase64(authenticationStore.userInfo.user.signCache, save)
        }
    }

    const getFromCanvas = async () => {
        setLoading(true);
        if (Platform.OS == "web") {
            let uriBase64 = signRef.current?.getCanvas().toDataURL("image/jpeg", 1);
            let resizeTo = [{ resize: { width: 400 } }];
            uriBase64 = await ImageManipulator.manipulateAsync(
                uriBase64, resizeTo,
                { compress: 0.7, format: ImageManipulator.SaveFormat.JPEG }
            );
            save(uriBase64.uri);
        } else {
            signRef.current.readSignature();
        }
    }

    const putStamp = async () => {
        if (Platform.OS == "web")
            signRef.current?.fromDataURL(authenticationStore.userInfo.user.stampCache);
        else
            convertImageToBase64(authenticationStore.userInfo.user.stampCache, signRef.current.loadDataURL);
    }

    const save = async (uri?: string | undefined) => {
        if (saveToCache) {
            authenticationStore.saveSignCache(uri);
        }
        let fileName: string = `${element.type}${rndExtraImg}-${element.id}-${itemsStore.itemActive._startedAt.getTime()}.jpg`;
        let uriSaved = await itemsStore.saveFilePayload(fileName, uri);
        checkAndSetPayload(uriSaved.uri);
        setModalVisible(false);
        setLoading(false);
        setSaveToCache(false);
    }

    const remove = async () => {
        setLoading(true);
        await itemsStore.removeFilePayload(payload.value)
        checkAndSetPayload(undefined);
        setLoading(false);
    }

    const canSign = () => {
        if (element.templateData.closeRecord) {        
            const form = itemsStore.getFormActive()                        
            let isvalid: boolean = checkIsValid(itemsStore, form, [element.id]);
            if(!isvalid)
                statusStore.setFlashMessage("Debes completar todos los campos obligatorios antes de firmar")
            return isvalid;
        } else
            return true;
    }

    // Preparación de la imagen de vista previa
    let mediaSource: any = null;
    let btnRemove = null;
    if (payload.value) {
        let uri = payload.value.toString();
        uri = uri.concat("?refresh=", new Date().getTime().toString());
        uri = ["file://", "http://", "https:/"].includes(uri.substring(0, 7)) ? uri : "file://".concat(uri);
        mediaSource = { uri };
        
        if (props.forReview !== true) {
            btnRemove = (
                <Pressable onPress={remove}>
                    <Icon as={Ionicons} name="trash" size="xl" color="$danger500" />
                </Pressable>
            );
        }
    }

    const webStyle = `.m-signature-pad {box-shadow: none; margin-top:0px;margin-left:1px; } 
              .m-signature-pad--body {left:0}
              .m-signature-pad--footer {display: none; margin: 0px;}
              body,html {
              width: ${dims.width.toFixed(0)}px; height: ${dims.height.toFixed(0)}px;}`;

    return (
        <>
            <ElementBase changedMe={loading} errors={errorList} element={element} forReview={props.forReview} rightButton={btnRemove}>
                {loading ? (
                    <HStack w="$full" justifyContent="center" p="$10">
                        <Spinner size="large" />
                    </HStack>
                ) : (
                    <VStack w="$full" space="md">
                        {mediaSource && (
                            <Box w="$full" h={150} bg="$white" mt="$2">
                                <Image 
                                    alt="signature" 
                                    source={mediaSource} 
                                    style={{ width: '100%', height: 150, resizeMode: "contain" }} 
                                />
                            </Box>
                        )}
                        {!props.forReview && (
                            <HStack justifyContent="center">
                                <Button variant="link" w="50%" onPress={() => canSign() && setModalVisible(true)}>
                                    <ButtonIcon as={MaterialCommunityIcons} name="signature-freehand" size="lg" mr="$2" />
                                    <ButtonText color="$secondary400">Firmar</ButtonText>
                                </Button>
                                {element.templateData.allowSignProfile && (
                                    <Button variant="link" w="50%" onPress={getFromCache}>
                                        <ButtonIcon as={MaterialIcons} name="verified-user" size="lg" mr="$2" />
                                        <ButtonText color="$secondary400">Traer de Perfil</ButtonText>
                                    </Button>
                                )}
                            </HStack>
                        )}
                    </VStack>
                )}
            </ElementBase>

            <ModalFull
                visible={modalVisible}
                title="Firmar"
                closeButton_onPress={() => setModalVisible(false)}
                rightButtons={authenticationStore.userInfo.user.stampCache ? (
                    <Pressable onPress={putStamp} style={{ marginRight: 15 }}>
                        <Icon as={FontAwesome5} name="stamp" color="$white" size="xl" />
                    </Pressable>
                ) : null}
                footer={
                    <ButtonGroup w="$full" isAttached={false} bg="$primary500" >
                        <Button variant="solid" bg="$primary500" w="50%" rounded="$none" onPress={getFromCanvas}>
                            <ButtonIcon as={Ionicons} name="checkmark" size="lg" mr="$2" />
                            <ButtonText>Aceptar</ButtonText>
                        </Button>
                        <Button variant="solid" bg="$primary400" w="50%" rounded="$none" onPress={clear}>
                            <ButtonIcon as={Ionicons} name="refresh" size="lg" mr="$2" />
                            <ButtonText>Limpiar</ButtonText>
                        </Button>
                    </ButtonGroup>
                }
            >
                <Box w={dims.width || 350} alignSelf="center" borderWidth={0} height={"$full"} justifyContent="center" >
                    {Platform.OS === "web" ? (
                        <SignatureCanvas
                            ref={signRef}
                            backgroundColor="#ffff"
                            minWidth={1}
                            maxWidth={5}
                            canvasProps={{
                                width: dims.width || 350,
                                height: dims.height || 200,
                            }}
                        />
                    ) : (
                        <View style={{ height: dims.height }}>
                            <SignatureScreen
                                ref={signRef}
                                onOK={save}
                                webStyle={webStyle}
                            />
                        </View>
                    )}
                </Box>
            </ModalFull>

            {/* AlertDialog optimizado con Gluestack */}
            <AlertDialog isOpen={alertData !== undefined} onClose={() => setAlertData(undefined)} finalFocusRef={cancelRef}>
                <AlertDialogBackdrop />
                <AlertDialogContent>
                    <AlertDialogHeader borderBottomWidth="$0">
                        <Heading size="lg">{alertData?.title}</Heading>
                    </AlertDialogHeader>
                    <AlertDialogBody>
                        <Text size="sm">{alertData?.message}</Text>
                    </AlertDialogBody>
                    <AlertDialogFooter borderTopWidth="$0">
                        <ButtonGroup space="lg">
                            <Button variant="outline" action="secondary" onPress={() => setAlertData(undefined)} ref={cancelRef}>
                                <ButtonText>Cancelar</ButtonText>
                            </Button>
                            <Button action="negative" bg="$danger600" onPress={() => { alertData?.action(); setAlertData(undefined); }}>
                                <ButtonText>Aceptar</ButtonText>
                            </Button>
                        </ButtonGroup>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
});